// Agent-defined custom columns for the Password Vault and Company Information
// tables. The column DEFINITIONS ([{ id, name }]) are agency-shared and live in
// AgencySetting.vaultCustomColumns / .companyCustomColumns; the per-row VALUES
// live on each row's `customData` ({ [columnId]: "text" }). Same shared-defs /
// per-row-values split as policyCustomFields.js and customTabDefs.
//
// These are pure helpers — the calling view owns persistence (updateAgencySettings)
// and React state. The stable `id` decouples a column from its display name, so a
// rename never orphans the values already typed into each row.
//
// EL TIPO DE LA COLUMNA (Lissette 2026-09-10): *"que cuando se cree la columna
// [se pueda elegir] la opcion de que sea escrito, o un drop down que se vaya
// creando a medida que se escribe, o un check"*. Son los tres de `TIPOS_DE_COLUMNA`:
//
//   text  — lo de siempre: texto libre.
//   list  — desplegable que SE LLENA SOLO. Las opciones no se guardan en ningun
//           sitio aparte: son las que la agencia ya escribio en esa columna
//           (`opcionesDeColumna`), mas las que alguien haya querido dejar puestas
//           de antemano en `col.options`. Deducirlas de lo escrito es la unica
//           copia que no se puede desincronizar — la misma decision que ya se tomo
//           con los temas de Capacitacion (`distinctLabels`) y con el tipo de
//           suplemento (2026-09-08): sugerencias, nunca un enum cerrado.
//   check  — una casilla marcable. Se guarda `MARCA_SI` ("1") o nada, porque el
//           servidor tira las casillas vacias y asi "sin marcar" no ocupa fila.
//
// Una columna SIN `type` es `text`: es lo que hay guardado hoy en las seis tablas
// que ya tenian columnas propias, y tiene que seguir viendose igual.

import { distinctLabels } from './utils.js';

/** Los tres tipos. El primero es el de siempre y el que vale por defecto. */
export const TIPOS_DE_COLUMNA = ['text', 'list', 'check'];

/** Lo que se guarda en una casilla marcada. Sin marcar no se guarda nada. */
export const MARCA_SI = '1';

/** Cuantas opciones se pueden dejar puestas de antemano en una columna `list`. */
export const MAX_OPCIONES = 50;

// A short, stable id for a new column.
export function genColumnId() {
  return 'c' + Math.random().toString(36).slice(2, 10);
}

/** El tipo de una columna, tolerando lo que ya esta guardado (sin `type`). */
export function tipoDeColumna(col) {
  const t = String(col?.type ?? '').trim().toLowerCase();
  return TIPOS_DE_COLUMNA.includes(t) ? t : 'text';
}

/**
 * Las opciones que alguien dejo puestas a mano. Se conserva EL ORDEN en que se
 * escribieron —es el que tiene sentido para quien las puso— y solo se quitan las
 * repetidas sin mirar mayusculas ni tildes, igual que `distinctLabels`.
 */
export function normalizeOptions(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  const vistos = new Set();
  for (const raw of arr) {
    if (out.length >= MAX_OPCIONES) break;
    const s = String(raw ?? '').trim().slice(0, 60);
    if (!s) continue;
    // El rango de tildes va ESCAPADO (\u0300-\u036f) y no con los signos
    // literales: son marcas combinantes, invisibles en el editor. Misma regla
    // que en `distinctLabels`.
    const clave = s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    out.push(s);
  }
  return out;
}

/**
 * Lo que ofrece el desplegable de una columna `list`: lo que alguien dejo puesto
 * MAS todo lo que la agencia ya escribio en esa columna. Asi la lista se crea
 * sola a medida que se escribe, que es justo lo que se pidio.
 *
 * `valores` son los valores de esa columna en las filas que hay a la vista.
 */
export function opcionesDeColumna(col, valores) {
  return distinctLabels([...(Array.isArray(col?.options) ? col.options : []), ...(valores || [])]);
}

/** True si esa casilla de tipo `check` esta marcada. */
export function estaMarcada(valor) {
  const s = String(valor ?? '').trim().toLowerCase();
  if (!s) return false;
  // "1" es lo que se guarda hoy. Los demas son por si una columna que ya tenia
  // texto escrito se cambia a `check`: "si", "yes" o una equis se leen como
  // marcadas en vez de como un valor raro que nadie entiende.
  return s === MARCA_SI || s === 'si' || s === 'sí' || s === 'yes' || s === 'true' || s === 'x';
}

// ── FILTRAR POR UNA COLUMNA PROPIA ─────────────────────────────────────────
//
// Crismar Perez (Maricarmen Nieto), 2026-09-10: *"queria consultarte si es
// posible que las columnas que agreguemos en renovacion tengan la opcion para
// filtrar a los clientes, como las otras columnas. Porque nuestra idea seria al
// final filtrar todos los clientes que no tengan ID me y volver a contactarlos"*.
//
// LO QUE PIDE DE VERDAD ES «SIN VALOR», y por eso esa opcion se ofrece SIEMPRE
// aunque hoy no falte ninguna: encontrar a quien le falta algo es para lo que
// sirve una columna que la agencia se inventa. Es la misma leccion de E-146 —
// un filtro sin una opcion "Sin ___" no deja encontrar lo que hay que arreglar.

/** Lo que vale el desplegable cuando se piden las filas SIN nada escrito. */
export const SIN_VALOR = '__sin_valor__';

/**
 * La clave con la que dos valores son EL MISMO: sin espacios de sobra, sin
 * mayusculas y sin tildes.
 *
 * La usan las dos mitades a proposito. `distinctLabels` agrupa con ella para
 * armar el desplegable, asi que "Creada", "creada" y "CREADA " se ofrecen una
 * sola vez; si al filtrar se comparara con `===` contra la forma ofrecida, las
 * otras dos no saldrian y el contador diria una cosa y la tabla otra. Agrupar
 * antes de ofrecer y comparar con lo mismo con que se agrupo son la misma regla
 * vista dos veces (E-146).
 */
export function claveDeValor(v) {
  return String(v ?? '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Lo que ofrece el desplegable de filtro de una columna: `[{ value, label }]`,
 * sin la opcion "Todas" — esa la pone la pantalla, que es quien sabe como se
 * llama en su idioma.
 *
 * `valores` son los de esa columna EN LAS FILAS A LA VISTA, no en las ya
 * filtradas: un filtro puesto no puede recortar la lista de lo que se puede
 * elegir, o al elegir uno desaparecerian los demas.
 */
export function opcionesDeFiltroDeColumna(col, valores, lang = 'es') {
  const es = lang === 'es';
  if (tipoDeColumna(col) === 'check') {
    return [
      { value: MARCA_SI, label: es ? '☑ Marcadas' : '☑ Ticked' },
      { value: SIN_VALOR, label: es ? '☐ Sin marcar' : '☐ Unticked' },
    ];
  }
  return [
    { value: SIN_VALOR, label: es ? '— Sin valor' : '— Empty' },
    ...distinctLabels(valores).map((v) => ({ value: v, label: v })),
  ];
}

/** True si esa casilla pasa el filtro elegido. Sin filtro pasa todo. */
export function pasaFiltroDeColumnaPropia(col, valor, filtro) {
  if (!filtro || filtro === 'all') return true;
  if (tipoDeColumna(col) === 'check') {
    return filtro === MARCA_SI ? estaMarcada(valor) : !estaMarcada(valor);
  }
  const vacio = !String(valor ?? '').trim();
  if (filtro === SIN_VALOR) return vacio;
  return !vacio && claveDeValor(valor) === claveDeValor(filtro);
}

/**
 * El valor de una casilla TAL Y COMO SE LEE: para las tablas que solo pintan
 * (Seguimientos, Baul, Info de Empresa) y para lo que se exporta a Excel.
 *
 * Sin esto, una columna `check` exportaria un "1" pelado, que no dice nada en
 * una hoja de calculo que va a leer una persona.
 */
export function textoDeCasilla(col, valor, lang = 'es') {
  if (tipoDeColumna(col) === 'check') {
    if (!estaMarcada(valor)) return '';
    return lang === 'es' ? 'Sí' : 'Yes';
  }
  return String(valor ?? '');
}

// Normalize an incoming list: trim names, drop blanks, dedupe by name
// (case-insensitive), ensure every column has a unique id, cap at 30.
export function normalizeColumns(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  const names = new Set();
  const ids = new Set();
  for (const raw of arr) {
    if (out.length >= 30) break;
    const name = String(raw?.name ?? '').trim().slice(0, 60);
    if (!name || names.has(name.toLowerCase())) continue;
    let id = String(raw?.id ?? '').trim().slice(0, 40);
    if (!id || ids.has(id)) id = genColumnId();
    names.add(name.toLowerCase());
    ids.add(id);
    // El tipo va SIEMPRE escrito, aunque sea el de siempre: una columna guardada
    // sin `type` no se distingue de una a la que le borraron el tipo, y ese es el
    // hueco por donde se cuela "elegi check y se guardo como texto".
    const col = { id, name, type: tipoDeColumna(raw) };
    // Las opciones solo tienen sentido en un desplegable. Guardarlas en una
    // columna de texto seria dejar un dato que nadie lee y que reaparece si algun
    // dia se le cambia el tipo.
    if (col.type === 'list') {
      const opciones = normalizeOptions(raw?.options);
      if (opciones.length) col.options = opciones;
    }
    // Optional per-column flag used only by the Monthly-Payments table: allow
    // pasting/attaching photos in that column's cells (Lissette 2026-07-28).
    // Other tables never set it, so their columns stay plain { id, name, type }.
    if (raw?.allowPhotos === true) col.allowPhotos = true;
    out.push(col);
  }
  return out;
}

/**
 * GUARDAR LAS COLUMNAS SIN BORRAR LAS QUE NO SE VEÍAN.
 *
 * Ticket de Marilyn Sequera, 2026-10-28 (urgente): *"estamos agregando dos
 * columnas extras en el tablero de renovaciones y se nos borran las otras
 * columnas que ya teníamos y perdimos información"*.
 *
 * El guardado mandaba la lista que tenía la ventana AL ABRIRSE, entera, y con
 * ella reemplazaba la guardada. Si esa lista llegó vacía (la carga de la tabla
 * falló o no había terminado) o vieja (otra persona del equipo añadió columnas
 * mientras tanto), las columnas que la ventana no conocía desaparecían — y con
 * ellas, en cuanto alguien tocaba otra casilla de la fila, sus valores.
 *
 * La regla ahora: solo se borra lo que la persona BORRÓ en la ventana. Una
 * columna que está en el servidor y que la ventana nunca vio se conserva.
 *
 *   alAbrir     — las columnas que la ventana recibió al abrirse.
 *   editadas    — las que devolvió al pulsar Guardar.
 *   delServidor — las guardadas AHORA, recién leídas.
 */
export function fusionarColumnas(alAbrir, editadas, delServidor) {
  const vistas = normalizeColumns(alAbrir);
  const nuevas = normalizeColumns(editadas);
  const guardadas = normalizeColumns(delServidor);
  const quedan = new Set(nuevas.map((c) => c.id));
  // Borrada = estaba en la ventana al abrirla y ya no está.
  const borradas = new Set(vistas.filter((c) => !quedan.has(c.id)).map((c) => c.id));
  const queNoSeVeian = guardadas.filter((c) => !quedan.has(c.id) && !borradas.has(c.id));
  return normalizeColumns([...nuevas, ...queNoSeVeian]);
}
